import {
  type ImportantContactBodyDtos,
  ImportantContactType,
  type ResidentBody,
  type ResidentItem
} from '@lmc/ts-common'
import { Checkbox, DatePicker, FormItem, Select } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import dayjs from 'dayjs'
import type { UpdateResidentReturnType } from 'hooks/useResident'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import BaseContactForm, { type BaseFormType } from './BaseContactForm'
import type { ImportantContactDependantDetailsForm } from './dependantDetailsFormTypes'
import { peelImportantContactFormFields } from './peelImportantContactFormFields'
import getOptionsWithOneMoreValue from './utils/getOptionsWithOneMoreValue'
import { relationshipOptions } from './utils/relationshipOptions'

export { ContactStatus } from '@lmc/ts-common'

export type FormType = BaseFormType &
  Pick<
    ImportantContactBodyDtos,
    'lpa' | 'observations' | 'contactableTimes'
  > & {
    isDependant?: boolean
    dependantDetails?: ImportantContactDependantDetailsForm
  }

type PersonalContactFormProps = {
  onSubmit: (values: Partial<ResidentBody>) => Promise<UpdateResidentReturnType>
  resident: ResidentItem | undefined
  isLoading: boolean
  editedIndex: number | undefined
  testId?: string
}

const PersonalContactForm = ({
  onSubmit,
  resident,
  isLoading,
  editedIndex,
  testId
}: PersonalContactFormProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)

  const getNewContact = (values: FormType): ImportantContactBodyDtos => {
    const { firstName, lastName, rest } = peelImportantContactFormFields(
      values as unknown as Record<string, unknown>
    )
    const isDependant = values.isDependant === true

    return {
      ...rest,
      type: ImportantContactType.personal,
      name: { first: firstName ?? '', last: lastName ?? '' },
      isDependant,
      dependantDetails: isDependant
        ? (values.dependantDetails ?? {})
        : undefined
    } as unknown as ImportantContactBodyDtos
  }

  const residentName =
    resident?.name?.first &&
    resident?.name.last &&
    `${resident.name.first} ${resident.name.last}'s`

  const editedContact =
    editedIndex !== undefined
      ? resident?.importantContacts[editedIndex]
      : undefined

  const options = useMemo(
    () =>
      getOptionsWithOneMoreValue(
        relationshipOptions,
        editedContact?.relationship
      ),
    [editedContact?.relationship]
  )
  const sortedOptions = options.sort((a, b) => (a.label > b.label ? 1 : -1))

  return (
    <BaseContactForm<FormType>
      editedIndex={editedIndex}
      getNewContact={getNewContact}
      includeDependantSection
      isLoading={isLoading}
      onSubmit={onSubmit}
      resident={resident}
      testId={testId}
      title={`${residentName} ${t(supConstants.PERSONAL_CONTACT)}`}
    >
      <FormItem label={t(supConstants.RELATIONSHIP_ROLE)} name="relationship">
        <Select
          optionRender={(option) => <Text>{option.label}</Text>}
          showSearch
          optionFilterProp="label"
          options={sortedOptions}
        />
      </FormItem>
      <FormItem
        label={t(supConstants.UNPAID_CARER)}
        name="unpaidCarer"
        valuePropName="checked"
      >
        <Checkbox />
      </FormItem>
      <FormItem
        label={t(supConstants.NEXT_OF_KIN)}
        name="nextOfKin"
        valuePropName="checked"
      >
        <Checkbox />
      </FormItem>
      <FormItem
        label={t(supConstants.DEPENDANT)}
        name="isDependant"
        valuePropName="checked"
      >
        <Checkbox />
      </FormItem>
      <FormItem
        noStyle
        shouldUpdate={(prev, curr) => prev?.isDependant !== curr?.isDependant}
      >
        {({ getFieldValue }) =>
          getFieldValue('isDependant') === true ? (
            <>
              <FormItem
                preserve={false}
                label={t(supConstants.DEPENDANT_DATE_OF_BIRTH)}
                name={['dependantDetails', 'dateOfBirth']}
                getValueProps={(value: string | undefined) => ({
                  value: value ? dayjs(value, 'YYYY-MM-DD') : undefined
                })}
                normalize={(value) =>
                  value ? dayjs(value).format('YYYY-MM-DD') : undefined
                }
              >
                <DatePicker format="DD-MM-YYYY" style={{ width: '100%' }} />
              </FormItem>
              <FormItem
                preserve={false}
                label={t(supConstants.DATE_OF_OBSERVATION)}
                name={['dependantDetails', 'dateOfObservation']}
                getValueProps={(value: string | undefined) => ({
                  value: value ? dayjs(value, 'YYYY-MM-DD') : undefined
                })}
                normalize={(value) =>
                  value ? dayjs(value).format('YYYY-MM-DD') : undefined
                }
              >
                <DatePicker format="DD-MM-YYYY" style={{ width: '100%' }} />
              </FormItem>
            </>
          ) : null
        }
      </FormItem>
    </BaseContactForm>
  )
}

export default PersonalContactForm
